
import { useEffect } from 'react';
import AnimatedSection from '../../components/base/AnimatedSection';

const GoalsPage = () => {
  useEffect(() => {
    document.title = 'أهدافنا | شركة الإعلام المتقدم - رؤيتنا ورسالتنا وقيمنا';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'تعرف على أهداف شركة الإعلام المتقدم، رؤيتنا المستقبلية، رسالتنا، وقيمنا الأساسية في تقديم خدمات إعلامية متميزة ومبتكرة');
    }
  }, []);

  const visionMission = [
    {
      title: 'رؤيتنا',
      description: 'أن نكون الشركة الرائدة في مجال الإعلام الرقمي والتسويق الإبداعي في المنطقة، ونساهم في تطوير المحتوى الإعلامي العربي وتعزيز الهوية الثقافية.',
      icon: 'ri-eye-line',
      color: 'bg-blue-500'
    },
    {
      title: 'رسالتنا',
      description: 'نلتزم بتقديم حلول إعلامية مبتكرة وخدمات تسويقية متطورة تساعد عملاءنا على تحقيق أهدافهم وبناء علامات تجارية قوية ومؤثرة في السوق.',
      icon: 'ri-target-line',
      color: 'bg-green-500'
    },
    {
      title: 'قيمنا',
      description: 'الإبداع والابتكار، الجودة والتميز، الشفافية والمصداقية، العمل الجماعي، التطوير المستمر، واحترام التنوع الثقافي والفكري.',
      icon: 'ri-heart-line',
      color: 'bg-red-500'
    }
  ];

  const strategicGoals = [
    {
      title: 'التوسع الجغرافي',
      description: 'الوصول إلى أسواق جديدة في المنطقة العربية وتوسيع قاعدة عملائنا',
      icon: 'ri-global-line',
      progress: 75
    },
    {
      title: 'الابتكار التقني',
      description: 'تطوير تقنيات جديدة في مجال الإعلام الرقمي والذكاء الاصطناعي',
      icon: 'ri-lightbulb-line',
      progress: 85
    },
    {
      title: 'تطوير المواهب',
      description: 'بناء فريق عمل متخصص ومدرب على أحدث التقنيات والأساليب',
      icon: 'ri-team-line',
      progress: 90
    },
    {
      title: 'الاستدامة البيئية',
      description: 'تطبيق ممارسات صديقة للبيئة في جميع عملياتنا وخدماتنا',
      icon: 'ri-leaf-line',
      progress: 60
    },
    {
      title: 'الشراكات الاستراتيجية',
      description: 'بناء تحالفات قوية مع الشركات الرائدة في مختلف القطاعات',
      icon: 'ri-handshake-line',
      progress: 70
    },
    {
      title: 'التحول الرقمي',
      description: 'مساعدة الشركات على التحول الرقمي وتطوير حضورها الإلكتروني',
      icon: 'ri-smartphone-line',
      progress: 95
    }
  ];

  const achievements = [
    { number: '500+', label: 'مشروع مكتمل', icon: 'ri-trophy-line' },
    { number: '200+', label: 'عميل راضي', icon: 'ri-user-smile-line' },
    { number: '50+', label: 'جائزة وتقدير', icon: 'ri-medal-line' },
    { number: '10+', label: 'سنوات خبرة', icon: 'ri-time-line' }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-32 bg-gradient-to-br from-blue-900 via-purple-900 to-indigo-900 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-20"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/7c18f4ed06155ec7d7a4278b7966661c.jpg')`
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-blue-900/80 to-purple-900/80" />
        
        <div className="container mx-auto px-4 relative z-10">
          <AnimatedSection animation="fadeIn" className="text-center text-white">
            <h1 className="text-5xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-white to-blue-200 bg-clip-text text-transparent">
              أهدافنا ورؤيتنا
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 max-w-4xl mx-auto leading-relaxed">
              نسعى لتحقيق التميز والريادة في مجال الإعلام الرقمي من خلال رؤية واضحة وأهداف طموحة
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Vision, Mission, Values */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <AnimatedSection animation="slideUp" className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">رؤيتنا ورسالتنا</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              نؤمن بقوة الإعلام في تشكيل المستقبل وبناء جسور التواصل بين الثقافات
            </p>
          </AnimatedSection>

          <div className="space-y-16">
            {visionMission.map((item, index) => (
              <AnimatedSection 
                key={index}
                animation={index % 2 === 0 ? "slideLeft" : "slideRight"}
                delay={index * 200}
                className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} items-center gap-12`}
              >
                <div className="flex-1">
                  <div className={`inline-flex items-center justify-center w-16 h-16 ${item.color} text-white rounded-2xl mb-6`}>
                    <i className={`${item.icon} text-2xl`}></i>
                  </div>
                  <h3 className="text-3xl font-bold text-gray-900 mb-4">{item.title}</h3>
                  <p className="text-lg text-gray-600 leading-relaxed">{item.description}</p>
                </div>
                <div className="flex-1">
                  <img 
                    src={`https://readdy.ai/api/search-image?query=Professional%20business%20$%7Bitem.title.toLowerCase%28%29%7D%20concept%20illustration%2C%20modern%20corporate%20environment%2C%20team%20collaboration%2C%20strategic%20planning%2C%20clean%20office%20space%2C%20inspiring%20atmosphere%2C%20blue%20and%20white%20color%20scheme%2C%20high%20quality%20business%20photography&width=600&height=400&seq=vision-${index}&orientation=landscape`}
                    alt={item.title}
                    className="w-full h-80 object-cover object-top rounded-2xl shadow-lg"
                  />
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Strategic Goals */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <AnimatedSection animation="slideUp" className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">أهدافنا الاستراتيجية</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              نعمل على تحقيق مجموعة من الأهداف الطموحة التي تضعنا في المقدمة
            </p>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {strategicGoals.map((goal, index) => (
              <AnimatedSection 
                key={index}
                animation="scaleUp"
                delay={index * 100}
                className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 p-8 border border-gray-100 group"
              >
                <div className="flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 text-white rounded-2xl mb-6 group-hover:scale-110 transition-transform duration-300">
                  <i className={`${goal.icon} text-2xl`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{goal.title}</h3>
                <p className="text-gray-600 mb-6 leading-relaxed">{goal.description}</p>
                
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-700">التقدم</span>
                    <span className="text-sm font-bold text-blue-600">{goal.progress}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-gradient-to-r from-blue-500 to-purple-600 h-2 rounded-full transition-all duration-1000"
                      style={{ width: `${goal.progress}%` }}
                    />
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Achievements */}
      <section className="py-20 bg-gradient-to-br from-blue-900 to-purple-900">
        <div className="container mx-auto px-4">
          <AnimatedSection animation="slideUp" className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">إنجازاتنا بالأرقام</h2>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              أرقام تتحدث عن مسيرتنا الناجحة وثقة عملائنا بنا
            </p>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {achievements.map((achievement, index) => (
              <AnimatedSection 
                key={index}
                animation="slideUp"
                delay={index * 150}
                className="text-center group"
              >
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 hover:bg-white/20 transition-all duration-300 group-hover:scale-105">
                  <div className="flex items-center justify-center w-16 h-16 bg-white/20 text-white rounded-2xl mx-auto mb-4 group-hover:bg-white/30 transition-colors duration-300">
                    <i className={`${achievement.icon} text-2xl`}></i>
                  </div>
                  <div className="text-4xl md:text-5xl font-bold text-white mb-2">{achievement.number}</div>
                  <div className="text-blue-100 font-medium">{achievement.label}</div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <AnimatedSection animation="slideUp" className="text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              انضم إلى رحلتنا نحو التميز
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8 leading-relaxed">
              كن جزءاً من قصة نجاحنا ودعنا نساعدك في تحقيق أهدافك الإعلامية والتسويقية
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="/contact"
                className="inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl whitespace-nowrap"
              >
                <i className="ri-phone-line ml-2"></i>
                تواصل معنا
              </a>
              <a 
                href="/services"
                className="inline-flex items-center justify-center px-8 py-4 border-2 border-blue-600 text-blue-600 font-semibold rounded-xl hover:bg-blue-600 hover:text-white transition-all duration-300 whitespace-nowrap"
              >
                <i className="ri-service-line ml-2"></i>
                استكشف خدماتنا
              </a>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
};

export default GoalsPage;
